import type { UseQueryResult } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useGetData } from '../../core/services/apiClient';
import { retryUnless401 } from '../../core/utils/retryPolicy';
import { POPUP_BANNER_ROUTES } from './popupBannerRoutes';
import type { PopupBanner } from './popupBanner.types';

/**
 * Fetch the single banner the server resolved for this user, or `null`.
 *
 * `null` is the normal resting state, not an error (FE-2): unlike the carousel there is no default
 * banner and no never-empty guarantee. The URL carries `siteCode` + `locale`, so the React Query
 * key changes when either changes and refetches automatically.
 */
export const usePopupBannerQuery = (
  siteCode: string,
  locale: string,
  enabled: boolean,
): UseQueryResult<PopupBanner | null, AxiosError> => {
  const baseUrl = import.meta.env.VITE_EXPERIENCE_API_BASE_URL as string;

  return useGetData<PopupBanner | null>(POPUP_BANNER_ROUTES.CURRENT(baseUrl, siteCode, locale), {
    enabled,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 10,
    retry: retryUnless401(),
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};
